import React, { useState, useEffect } from "react";
import SelectionTable from "./common/SelectionTable";
import CrudModal from "../../general/CrudModal";
import axios from "axios";
import { API_ENDPOINTS } from "../../../config/apiConfig";

const RouteSelection = ({ routes, loading, error, selectedRoutes, setSelectedRoutes, nextStep, onEditRoute }) => {
  // Add state for edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentEditItem, setCurrentEditItem] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);

  // Add new state for add route modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addLoading, setAddLoading] = useState(false);

  // Add these state variables at the top of your component after the existing state declarations
  const [filteredRoutes, setFilteredRoutes] = useState(routes);
  const [filterCriteria, setFilterCriteria] = useState({});

  // Add useEffect to update filtered routes when original routes change
  useEffect(() => {
    setFilteredRoutes(routes);
  }, [routes]);

  // Handle checkbox change
  const handleCheckboxChange = (routeIdOrArray, e) => {
    // Stop propagation if event is provided
    if (e) {
      e.stopPropagation();
    }
    
    setSelectedRoutes((prevSelected) => {
      // Handle array of IDs (for select all/none functionality)
      if (Array.isArray(routeIdOrArray)) {
        return routeIdOrArray;
      }
      // Handle single ID toggle
      const routeId = routeIdOrArray;
      if (prevSelected.includes(routeId)) {
        return prevSelected.filter((id) => id !== routeId);
      } else {
        return [...prevSelected, routeId];
      }
    });
  };

  // Handle "Select All" checkbox
  const handleSelectAllRoutes = (e) => {
    if (e) e.stopPropagation();
    if (allRoutesSelected) {
      // Deselect all routes
      handleCheckboxChange([]);
    } else {
      // Select all routes at once
      handleCheckboxChange(routes.map(route => route.id));
    }
  };

  const allRoutesSelected = routes.length > 0 && selectedRoutes.length === routes.length;

  // Define columns for the route table
  const routeColumns = [
    { key: 'raw_route_name', label: 'Route Name', width: '15%' },
    { key: 'origin', label: 'Origin', width: '22%' },
    { key: 'destination', label: 'Destination', width: '22%' },
    { key: 'distance', label: 'Travel Distance', width: '12%' },
    { key: 'start_time', label: 'Start Time', width: '12%' }
  ];

  // Use the column definitions in the render function
  const renderRouteRow = (route) => {
    const origin = route.addresses[0].toString();
    const destination = route.addresses[route.addresses.length - 1].toString();
    const totalDistance = parseFloat((route.distances.reduce((a, b) => a + b, 0) / 1609.34).toFixed(1));

    return (
      <>
        <td style={{ width: routeColumns[0].width }}>{route.raw_route_name}</td>
        <td style={{ width: routeColumns[1].width }}>{origin}</td>
        <td style={{ width: routeColumns[2].width }}>{destination}</td>
        <td style={{ width: routeColumns[3].width }}>{totalDistance} miles</td>
        <td style={{ width: routeColumns[4].width }}>{route.start_time}</td>
      </>
    );
  };

  // Update route fields to remove the origin and start_time since they'll be in the trip itinerary
  const route_fields = [
    { id: "route_name", name: "Route name", type: "string", required: true }
    // Origin and start_time removed as they're now part of the trip itinerary
  ];

  // Update handleEditRoute to properly set up formData for the new UI
  const handleEditRoute = (route) => {
    // Prepare the initial form data for editing
    const formData = {
      route_id: route.id,
      route_name: route.raw_route_name,
      origin: route.addresses[0],
      start_time: route.start_time,
      // Skip the first address (origin) for stops
      stops: route.addresses.slice(1),
      // Idle times should already be in the right format
      idle_times: route.stop_durs || []
    };
    
    // Set the edit item and open the modal
    setCurrentEditItem(formData);
    setIsEditModalOpen(true);
    setEditError(null);
  };

  // Handle save changes
  const handleSaveChanges = async (formData) => {
    setEditLoading(true);
    setEditError(null);
    
    try {
      // Get the auth token from localStorage
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error("Authentication token is missing");
      }
      
      // Prepare route data for the API
      const routeData = {
        route_id: formData.route_id,
        start_time: formData.start_time,
        stops: [formData.origin, ...(formData.stops || [])],
        route_name: formData.route_name,
        idle_times: formData.idle_times || []
      };

      // Call the API to update the route
      const response = await axios.put(
        API_ENDPOINTS.editRoute,
        routeData,
        {
          headers: {
            Authorization: `Token ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (response.status === 200) {
        // If onEditRoute is a function, call it to refresh the route data
        if (typeof onEditRoute === 'function') {
          onEditRoute(formData, true, "edit"); // Pass "edit" to indicate edit operation
        }
        
        // Close the modal
        setIsEditModalOpen(false);
        setCurrentEditItem(null);
      } else {
        throw new Error("Failed to update route");
      }
    } catch (error) {
      console.error("Error updating route:", error);
      setEditError(error.response?.data?.error || error.message || "An error occurred while updating the route");
    } finally {
      setEditLoading(false);
    }
  };

  // Add route handler
  const handleAddRoute = async (formData) => {
    setAddLoading(true);
    setAddError(null);
    
    try {
      // Get auth token
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error("Authentication token is missing");
      }
      
      // Create a new array with the origin as the first stop
      const stops = [formData.origin];
      
      // Add any additional stops from the formData.stops array
      if (formData.stops && formData.stops.length > 0) {
        stops.push(...formData.stops);
      }

      const routeData = {
        start_time: formData.start_time,
        stops: stops,
        route_name: formData.route_name,
        idle_times: formData.idle_times || [], // Ensure idle_times is always defined
      };

      const response = await axios.post(
        API_ENDPOINTS.addRoute,
        routeData,
        {
          headers: {
            Authorization: `Token ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      // If successful, call onEditRoute to refresh the routes list
      if (response.status === 200 || response.status === 201) {
        if (typeof onEditRoute === 'function') {
          onEditRoute(formData, true, "add");
        }
        
        // Close the modal
        setIsAddModalOpen(false);
      }
    } catch (error) {
      console.error("Error adding route:", error);
      // Improve error message with more user-friendly guidance
      let errorMessage = error.response?.data?.error || error.message || "An error occurred while adding the route";
      
      // Add more helpful context based on common error patterns
      if (errorMessage.includes("geocode") || errorMessage.includes("location") || 
          errorMessage.includes("address") || errorMessage.includes("coordinates")) {
        errorMessage = "Please try a more accurate address. We couldn't geocode one or more locations.";
      } else if (errorMessage.includes("Could not calculate") || errorMessage.includes("route") || errorMessage.includes("not found")) {
        errorMessage = "Couldn't find a route between the provided locations. Please provide more accurate addresses.";
      }
      
      // Set error state for the modal
      setAddError(errorMessage);
      
      // Also pass the error to the parent component to show a notification
      if (typeof onEditRoute === 'function') {
        // The false parameter indicates the operation was not successful
        onEditRoute({
          errorMessage: errorMessage
        }, false, "add");
      }
    } finally {
      setAddLoading(false);
    }
  };

  // Delete routes handler
  const handleDeleteSelectedRoutes = async () => {
    if (selectedRoutes.length === 0) {
      return;
    }
    
    try {
      // Get auth token
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error("Authentication token is missing");
      }
      
      // Send a POST request with the route_ids in the request body
      const response = await axios.post(
        API_ENDPOINTS.deleteRoute,
        { route_ids: selectedRoutes },
        {
          headers: {
            Authorization: `Token ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
       
      if (response.status >= 200 && response.status < 300) {
        // If successful, call onEditRoute to refresh the routes list
        if (typeof onEditRoute === 'function') {
          onEditRoute({}, true, "delete");
        }
      } else {
        throw new Error("Unexpected response status: " + response.status);
      }
    } catch (error) {
      console.error("Error deleting routes:", error);
    }
  };

  // Add a filter change handler
  const handleFilterChange = (filters) => {
    setFilterCriteria(filters);
    
    // If no filters, show all items
    if (Object.keys(filters).length === 0) {
      setFilteredRoutes(routes);
      return;
    }
    
    // Filter items based on criteria
    const filtered = routes.filter(route => {
      return Object.entries(filters).every(([columnKey, allowedValues]) => {
        if (!allowedValues || allowedValues.length === 0) return true;
        
        // Special handling for nested properties or computed columns
        if (columnKey === 'origin') {
          return allowedValues.includes(route.addresses[0].toString());
        } else if (columnKey === 'destination') {
          return allowedValues.includes(route.addresses[route.addresses.length - 1].toString());
        } else if (columnKey === 'distance') {
          // For distance, we need to compute it the same way as in renderRouteRow
          const totalDistance = parseFloat((route.distances.reduce((a, b) => a + b, 0) / 1609.34).toFixed(1));
          // Convert to string with "miles" to match what's displayed in the table
          return allowedValues.includes(`${totalDistance} miles`);
        } else {
          // For other simple properties
          return allowedValues.includes(route[columnKey]);
        }
      });
    });
    
    setFilteredRoutes(filtered);
  };

  if (loading) {
    return <div className="step-container"><p>Loading routes...</p></div>;
  }

  if (error) {
    return <div className="step-container"><p className="error">{error}</p></div>;
  }

  return (
    <div className="step-container">
      {/* Add the route action buttons */}
      <div className="entity-actions route-actions">
        <CrudModal 
          entity="Route"
          mode="add"
          fields={route_fields}
          onSubmit={handleAddRoute}
          buttonText="Add Route"
          dynamicAddRoute={true}
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          isLoading={addLoading}
          error={addError}
        />
        
        {selectedRoutes.length > 0 && (
          <CrudModal 
            entity="Route"
            mode="delete"
            onSubmit={handleDeleteSelectedRoutes}
            selectedItems={routes.filter(route => selectedRoutes.includes(route.id))}
            buttonPosition="right"
          />
        )}
      </div>

      {routes.length === 0 ? (
        <p>No routes found. Please add routes to continue.</p>
      ) : (
        <>
          <div className="selection-summary">
            <p>
              Showing {filteredRoutes.length} routes 
              ({selectedRoutes.length} selected)
            </p>
          </div>
          <SelectionTable
            items={routes}
            selectedItems={selectedRoutes}
            handleCheckboxChange={handleCheckboxChange}
            handleSelectAll={handleSelectAllRoutes}
            allSelected={allRoutesSelected}
            columns={routeColumns}
            itemIdKey="id"
            renderRow={renderRouteRow}
            onEditItem={handleEditRoute}
            filteredItems={filteredRoutes}
            onFilterChange={handleFilterChange}
          />
        </>
      )}

      {/* Add the edit modal */}
      {isEditModalOpen && currentEditItem && (
        <div className="modal-overlay">
          <CrudModal
            entity="Route"
            mode="edit"
            fields={route_fields}
            onSubmit={handleSaveChanges}
            selectedItems={[currentEditItem]}
            initialData={currentEditItem}
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            isLoading={editLoading}
            error={editError}
            dynamicAddRoute={true}
          />
        </div>
      )}
    </div>
  );
};

export default RouteSelection; 